import type { MapPin, ItineraryMode } from "./types";
import { supabase } from "@/lib/supabase";

/** Attach Authorization header when a Supabase session exists. Never logs token. */
async function _getAuthHeaders(): Promise<Record<string, string>> {
  if (!supabase) return {};
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      return { Authorization: `Bearer ${session.access_token}` };
    }
  } catch {
    // Non-fatal: request proceeds without auth header
  }
  return {};
}

// Staging & Release Lane V1 — the one known production backend hostname
// (same default already used server-side as scripts/verify_provider_
// landing.py's DEFAULT_BASE_URL). Pure, exported, and unit-tested so a
// staging build that's accidentally still pointed at the production
// backend — sharing its Supabase writes, operational metrics, and Viator
// affiliate traffic — fails loudly instead of silently misbehaving.
export const PRODUCTION_API_HOSTNAME = "rhea-backend-7uni.onrender.com";

export function isProductionApiBaseUrl(url: string): boolean {
  try {
    return new URL(url).hostname === PRODUCTION_API_HOSTNAME;
  } catch {
    return false;
  }
}

export const API_BASE =
  (import.meta.env.VITE_RHEA_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ||
  "http://localhost:8000";

/**
 * Fail loudly (never silently) when a staging build is misconfigured to
 * talk to the production backend — this would mean staging traffic writes
 * to production Supabase, production operational metrics, and production
 * Viator affiliate attribution, exactly what the staging lane exists to
 * prevent. `env` is VITE_RHEA_ENV's value — the existing, already-deployed
 * environment label (see src/lib/sentry.ts); no new env var invented here.
 * Extracted as a pure function (rather than an inline module-level `if`)
 * so every branch is directly unit-testable.
 */
export function assertStagingNeverUsesProductionApiBase(env: string | undefined, apiBase: string): void {
  if (env === "staging" && isProductionApiBaseUrl(apiBase)) {
    throw new Error(
      "[SiftTrip] STAGING MISCONFIGURATION: VITE_RHEA_ENV=staging but " +
      `VITE_RHEA_API_BASE_URL points at the production backend (${PRODUCTION_API_HOSTNAME}). ` +
      "Refusing to proceed — staging must never read/write against production.",
    );
  }
}

assertStagingNeverUsesProductionApiBase(import.meta.env.VITE_RHEA_ENV as string | undefined, API_BASE);

let coldStartListeners: Array<(visible: boolean) => void> = [];

export function onColdStart(cb: (visible: boolean) => void) {
  coldStartListeners.push(cb);
  return () => { coldStartListeners = coldStartListeners.filter((l) => l !== cb); };
}

function notifyColdStart(visible: boolean) {
  coldStartListeners.forEach((cb) => cb(visible));
}

/** Force-hides the cold-start banner immediately.
 *  Safe to call from any route after a request completes. */
export function clearColdStart() {
  notifyColdStart(false);
}

async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 120000); // 120s — warm backend processes in <30s
  const coldTimer = setTimeout(() => notifyColdStart(true), 2000); // show banner after 2s

  try {
    const authHeaders = await _getAuthHeaders();
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
        ...options?.headers,
      },
    });
    if (!res.ok) {
      throw new Error(`API error: ${res.status} ${res.statusText}`);
    }
    return res.json();
  } finally {
    clearTimeout(timeout);
    clearTimeout(coldTimer);
    notifyColdStart(false);
  }
}

// ─── wakeServer — cached awaitable promise ────────────────────────────────────
// Returns a Promise that resolves once the backend confirms it's awake (or after
// 65s max if Render is slow to respond).  The same promise is reused across all
// callers so only one /wake request ever fires per page load.
// generateSequence awaits this to ensure the backend is warm before the heavier
// /experiences/sequence POST lands — eliminates the cold-start race condition.
let _wakePromise: Promise<void> | null = null;

export function wakeServer(): Promise<void> {
  if (!_wakePromise) {
    const ctrl = new AbortController();
    setTimeout(() => ctrl.abort(), 65000); // 65s hard cap for wake
    _wakePromise = fetch(`${API_BASE}/wake`, { method: "GET", signal: ctrl.signal })
      .then(() => {})
      .catch(() => {}); // always resolve — let the sequence call handle errors
  }
  return _wakePromise;
}

export async function searchAirports(q: string) {
  if (!q || q.length < 2) return [];
  const res = await fetchApi<{ results?: Array<{ iata: string; name: string; city: string; country: string }> } | Array<{ iata: string; name: string; city: string; country: string }>>(
    `/search/airports?q=${encodeURIComponent(q)}`
  );
  if (Array.isArray(res)) return res;
  return res.results || [];
}

// Flight types
export interface FlightResult {
  airline: string;
  airline_logo?: string;
  flight_number?: string;
  departure_time?: string;
  arrival_time?: string;
  departure_city?: string;
  arrival_city?: string;
  duration?: string;
  duration_minutes?: number;
  price?: number;
  currency?: string;
  cabin_class?: string;
  stops?: number;
  carbon_emissions?: { percentage_diff?: number };
  booking_url?: string;
  ignav_id?: string;
  source?: string;
  provider?: string;
  is_best?: boolean;
}

export interface PriceInsights {
  lowest?: number;
  lowest_price?: number;
  typical_range_low?: number;
  typical_range_high?: number;
  typical_price_range?: [number, number];
  price_level?: string;
}

export interface FlightSearchParams {
  origin: string;
  destination: string;
  date: string;
  adults?: number;
  cabin_class?: string;
  max_price?: number;
  max_stops?: number;
}

export interface FlightSearchResponse {
  success?: boolean;
  results?: FlightResult[];
  flights?: FlightResult[];
  price_insights?: PriceInsights;
  providers_used?: string[];
  total_results?: number;
}

export async function searchFlights(params: FlightSearchParams): Promise<FlightSearchResponse> {
  const qs = new URLSearchParams();
  qs.set("origin", params.origin);
  qs.set("destination", params.destination);
  qs.set("date", params.date);
  if (params.adults) qs.set("adults", String(params.adults));
  if (params.cabin_class) qs.set("cabin_class", params.cabin_class);
  if (params.max_price) qs.set("max_price", String(params.max_price));
  if (params.max_stops !== undefined) qs.set("max_stops", String(params.max_stops));
  return fetchApi<FlightSearchResponse>(`/search/flights/smart?${qs.toString()}`);
}

export async function getBookingLinks(params: {
  ignav_id: string;
  origin: string;
  destination: string;
  departure_date: string;
  adults: number;
}) {
  const qs = new URLSearchParams({
    ignav_id: params.ignav_id,
    origin: params.origin,
    destination: params.destination,
    departure_date: params.departure_date,
    adults: String(params.adults),
  });
  return fetchApi<{ booking_options: Array<{ airline: string; logo?: string; price?: number; url: string }> }>(
    `/search/flights/ignav/booking-links?${qs.toString()}`
  );
}

export async function getIntermediateStops(from_iata: string, to_iata: string) {
  return fetchApi<{ suggestions: Array<{ city: string; iata: string; reason: string }> }>(
    `/route/intermediate-stops?from_iata=${from_iata}&to_iata=${to_iata}`
  );
}

export async function compareTransport(params: {
  from_city: string;
  from_iata: string;
  to_city: string;
  to_iata: string;
  date: string;
  adults?: number;
}) {
  const qs = new URLSearchParams({
    from_city: params.from_city,
    from_iata: params.from_iata,
    to_city: params.to_city,
    to_iata: params.to_iata,
    date: params.date,
    adults: String(params.adults || 1),
  });
  return fetchApi<{ options: Array<{ mode: string; price?: number; duration?: string; operator?: string; currency?: string }> }>(
    `/compare/transport?${qs.toString()}`
  );
}

// Route suggestion types (real API format)
export interface RouteDestination {
  city: string;
  country?: string;
  iata: string;
  nights: number;
  lat?: number;
  lng?: number;
  highlights?: string[];
  best_for?: string;
}

export interface RouteSuggestion {
  route_name?: string;
  name?: string;
  tagline?: string;
  destinations?: RouteDestination[];
  cities?: Array<{ city: string; iata: string; nights: number }>;
  total_estimated_usd?: number;
  estimated_cost?: number;
  within_budget?: boolean;
  budget_used_pct?: number;
  transport_notes?: string;
  rhea_tip?: string;
  intermediate_suggestions?: Array<{ city: string; iata: string; reason: string }>;
  intermediate_suggestion?: string;
}

export interface RouteSuggestParams {
  region: string;
  budget_usd: number;
  days: number;
  travelers: number;
  origin_iata: string;
  mood: string;
}

export async function suggestRoutes(params: RouteSuggestParams): Promise<{ routes: RouteSuggestion[] }> {
  return fetchApi<{ routes: RouteSuggestion[] }>("/route/suggest", {
    method: "POST",
    body: JSON.stringify({
      origin: params.origin_iata,
      region: params.region,
      budget_usd: params.budget_usd,
      days: params.days,
      travelers: params.travelers,
      mood: params.mood,
      flexible_cities: true,
    }),
  });
}

// Itinerary types (real API format)
// Legacy day shape returned by /itinerary/create (old endpoint, pre-Rhea-2.0).
// Not imported externally — canonical shape is TimelineDay in types.ts.
interface ItineraryDayActivity {
  time?: string;
  description?: string;
  activity?: string;
}

interface ItineraryDay {
  day: number;
  city: string;
  title: string;
  morning?: string;
  afternoon?: string;
  evening?: string;
  meals?: string;
  activities?: ItineraryDayActivity[];
  hotel_suggestion?: string;
}

// MapPin and ItineraryMode are canonical in types.ts — imported at top of file.

export interface ItineraryResponse {
  itinerary?: { days?: ItineraryDay[] };
  days?: ItineraryDay[];
  real_flights?: Array<{ origin: string; destination: string; price?: number; airline?: string }>;
  map_pins?: MapPin[];
  events_by_city?: Record<string, Array<{ name: string; date?: string; venue?: string; url?: string; is_free?: boolean }>>;
  total_cost?: number;
  summary?: string;
}

export async function createItinerary(params: {
  origin: string;
  destinations: Array<{
    city: string;
    country?: string;
    iata: string;
    checkin: string;
    checkout: string;
    nights: number;
  }>;
  travelers: number;
  budget_usd: number;
  mood?: string;
}): Promise<ItineraryResponse> {
  return fetchApi<ItineraryResponse>("/itinerary/create", {
    method: "POST",
    body: JSON.stringify({
      origin: params.origin,
      destinations: params.destinations,
      travelers: params.travelers,
      budget_usd: params.budget_usd,
      budget_level: "medio",
      mood: params.mood || "cultura",
      language: "es",
      include_hotels: true,
      include_flights: true,
      include_events: true,
    }),
  });
}

export async function searchHotels(params: {
  city: string;
  checkin: string;
  checkout: string;
  adults?: number;
  rooms?: number;
  currency?: string;
}) {
  const qs = new URLSearchParams({
    city: params.city,
    checkin: params.checkin,
    checkout: params.checkout,
    adults: String(params.adults || 2),
    rooms: String(params.rooms || 1),
    currency: params.currency || "USD",
  });
  return fetchApi<{ hotels: Array<{ name: string; price?: number; rating?: number; url?: string }> }>(
    `/search/hotels/makcorps?${qs.toString()}`
  );
}

export async function searchEvents(params: {
  city: string;
  start_date?: string;
  end_date?: string;
  max_results?: number;
}) {
  const qs = new URLSearchParams({ city: params.city });
  if (params.start_date) qs.set("start_date", params.start_date);
  if (params.end_date) qs.set("end_date", params.end_date);
  if (params.max_results) qs.set("max_results", String(params.max_results));
  return fetchApi<{ events: Array<{ name: string; date?: string; venue?: string; url?: string; is_free?: boolean }> }>(
    `/search/events?${qs.toString()}`
  );
}

export interface LocalInfo {
  neighborhoods?: string;
  food?: string;
  transport?: string;
  local_tip?: string;
  safety?: string;
}

export async function getLocalInfo(city: string, iata: string): Promise<LocalInfo> {
  return fetchApi<LocalInfo>(`/destination/local-info?city=${encodeURIComponent(city)}&iata=${iata}`);
}

// ---------------------------------------------------------------------------
// Rhea 2.0 Engine — /itinerary/generate
// ---------------------------------------------------------------------------
// ItineraryMode is canonical in types.ts — imported at top of file.
// Re-export for consumers that import it from api.ts (backward compat).
export type { ItineraryMode };

export interface FixedCity {
  iata: string;
  city: string;
  nights: number;
}

export interface GenerateItineraryParams {
  origin: string;
  region: string;
  destination?: string;         // Free-text destination (resolver will parse)
  budget_usd: number;
  days: number;
  travelers?: number;
  mood?: string;
  mode?: ItineraryMode;
  departure_date?: string;
  n_cities?: number;
  fixed_cities?: FixedCity[];   // User-defined route (FixedCitiesForm)
  // Sprint 2 — Route Intelligence contract
  route_cities?: unknown[];     // RouteCity[] from intentParser (parsed JSON from URL)
  open_constraints?: unknown[]; // OpenConstraint[] from intentParser
  multi_city?: boolean;
  // Mobility Preference Scoring
  mobility_preference?: string; // Comma-separated: "prefer_train,low_friction"
  // Temporal Planning Engine v1 — "YYYY-MM" for flexible/inferred start date
  travel_month_ym?: string;
}

// Resolver metadata block returned inside a successful /itinerary/generate response
export interface ResolverMeta {
  destination_query: string;
  resolved_name: string;
  entity_type: EntityType;
  planning_tier: PlanningTier;
  continent_id: string | null;
  country_iso2: string | null;
  candidate_pool_size: number;
  candidate_pool: string[];
  discarded_candidates: string[];
  t1_parts_skipped: string[];
  is_literal_multi_city: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function generateItinerary(params: GenerateItineraryParams): Promise<any> {
  return fetchApi("/itinerary/generate", {
    method: "POST",
    body: JSON.stringify({ travelers: 2, mood: "cultura", mode: "smooth", ...params }),
  });
}

// ---------------------------------------------------------------------------
// Trip Interpretation — /trip/interpret
// ---------------------------------------------------------------------------
export interface TripInterpretResult {
  success: boolean;
  moods: string[];
  mode: "budget" | "smooth" | "experience";
  comfort: "budget" | "mid-range" | "premium";
  rhythm: "slow" | "moderate" | "fast";
  n_cities: number;
  summary: string;
}

export async function interpretTrip(text: string): Promise<TripInterpretResult> {
  return fetchApi("/trip/interpret", {
    method: "POST",
    body: JSON.stringify({ text }),
  });
}

// ---------------------------------------------------------------------------
// Hotels for Stay — /hotels/for-stay
// ---------------------------------------------------------------------------
export interface HotelOption {
  id: string;
  name: string;
  type: "budget" | "comfortable" | "boutique" | "luxury";
  type_label: string;
  stars: number;
  rating: number;
  price_per_night: number;
  total_price: number;
  neighborhood: string;
  amenities: string[];
  source: string;
}

export interface HotelsForStayResult {
  success: boolean;
  city: string;
  city_iata: string;
  nights: number;
  checkin: string | null;
  checkout: string | null;
  recommended: HotelOption[];
  alternatives: HotelOption[];
}

// ---------------------------------------------------------------------------
// Transport for Leg — /transport/for-leg
// ---------------------------------------------------------------------------
export interface TransportOption {
  id: string;
  mode: "flight" | "train" | "bus" | "ferry";
  carrier: string;
  duration_hours: number;
  duration_label: string;
  price: number;
  total_price: number;
  stops: number;
  friction_score: number;
  decision_label: string;
  is_recommended: boolean;
}

export interface TransportForLegResult {
  success: boolean;
  from_iata: string;
  to_iata: string;
  from_city: string;
  to_city: string;
  departure_date: string | null;
  travelers: number;
  recommended: TransportOption | null;
  alternatives: TransportOption[];
}

export async function searchTransportForLeg(params: {
  from_iata: string;
  to_iata: string;
  departure_date?: string;
  travelers: number;
  mode_preference?: string;
}): Promise<TransportForLegResult> {
  return fetchApi("/transport/for-leg", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function searchHotelsForStay(params: {
  city_iata: string;
  city_name: string;
  checkin?: string;
  checkout?: string;
  nights: number;
  travelers: number;
}): Promise<HotelsForStayResult> {
  return fetchApi("/hotels/for-stay", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

// ---------------------------------------------------------------------------
// Location Resolver
// ---------------------------------------------------------------------------

export interface LocationResolvedPart {
  query: string;
  matched_type: "city" | "country" | "continent" | "region" | "iata" | "airport_group" | "unknown";
  matched_name: string;
  iata_pool: string[];
  primary_iata: string | null;
  continent_id: string | null;
  country_iso2: string | null;
  confidence: number;
  notes: string[];
}

export interface LocationResult {
  success: boolean;
  query: string;
  multi: boolean;
  // Single resolution
  matched_type?: LocationResolvedPart["matched_type"];
  matched_name?: string;
  iata_pool?: string[];
  primary_iata?: string | null;
  continent_id?: string | null;
  country_iso2?: string | null;
  confidence?: number;
  notes?: string[];
  resolved?: boolean;
  // Multi resolution
  parts?: LocationResolvedPart[];
  merged_pool?: string[];
  // Error
  error?: string;
}

export async function resolveLocation(query: string): Promise<LocationResult> {
  const params = new URLSearchParams({ q: query });
  return fetchApi(`/location/resolve?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Location Autocomplete
// ---------------------------------------------------------------------------

export type PlanningTier = "curated" | "full" | "resolver_only" | "unknown";
export type EntityType = "city" | "airport" | "country" | "continent" | "region";

export interface AutocompleteSuggestion {
  id: string;
  entity_type: EntityType;
  iata: string | null;
  nearest_airport_iata: string | null;
  name: string;
  name_en: string;
  country_iso2: string | null;
  country_name?: string | null;
  continent_id: string | null;
  lat: number | null;
  lng: number | null;
  planning_tier: PlanningTier;
  popularity_score: number;
  transport_nodes: Array<{ type: string; id: string; name: string }>;
  /**
   * Sprint: Destination Marketplace Identity vs Nearest Airport Separation
   * V1, Parte E — optional, additive. Only populated when this suggestion
   * came from DestinationPicker's destinationCandidateToSuggestion()
   * (never by autocompleteLocations()'s own /location/autocomplete path,
   * which predates this contract) — every existing consumer that ignores
   * these fields keeps working unchanged. Forwarded into
   * MarketplaceSearchContext so `/experiences/search` can use the real
   * destination identity instead of re-deriving it from `iata`, which may
   * only be a nearby airport's code.
   */
  destination_id?:            string;
  marketplace_search_name?:   string;
  destination_identity_kind?: string;
  /**
   * Sprint: Destination Picker UX V1.2 — same additive/optional contract as
   * the 3 fields above (only ever populated via destinationCandidateToSuggestion(),
   * mirrors DestinationCandidate.is_airport_proxy 1:1). Lets the picker's
   * own result row show an honest "nearby airport" note for a destination
   * with no code of its own (e.g. Rovinj borrowing PUY) instead of either
   * hiding the airport info entirely or implying it's the destination's
   * own identity.
   */
  is_airport_proxy?: boolean;
}

export interface AutocompleteResult {
  success: boolean;
  query: string;
  results: AutocompleteSuggestion[];
  error?: string;
}

export async function autocompleteLocations(
  q: string,
  limit = 8
): Promise<AutocompleteResult> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  return fetchApi(`/location/autocomplete?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Destination Catalog Expansion + Ambiguous Alias Resolution V1, Parte E —
// client for the GET /destinations/search endpoint (engine.cities.
// destination_catalog_resolver). DestinationPicker.tsx uses this as its
// PRIMARY source for city-level candidates; Sprint: Search Scope +
// Locale-Stable Inventory + Traveler Type Label Hotfix V1 additionally
// merges in autocompleteLocations() above (filtered to its country/
// continent/region rows only) so country/continent/region search — which
// this resolver deliberately never returns (see destination_catalog_
// resolver.py's own module doc) — keeps working. match_type/reason_code
// are the same small fixed vocabularies the backend resolver returns —
// see that module's own docstring for the full match precedence
// (iata > exact > airport/alias > fuzzy) and ambiguity_group contract
// (multiple candidates sharing a group must never be collapsed to one).
// ---------------------------------------------------------------------------

export type DestinationMatchType = "exact" | "alias" | "iata" | "airport" | "fuzzy";

export interface DestinationCandidate {
  canonical_id: string;
  display_name: string;
  country_name: string;
  country_code: string;
  city_name: string;
  /** This destination's OWN code — null when it has none (e.g. a small
   *  town whose only usable code is a nearby city's airport). Never use
   *  this as a fallback "is this destination searchable" check — use
   *  `nearest_airport_iata` for that instead. */
  iata_code: string | null;
  /**
   * Hotfix: Destination Picker Uses Expanded Catalog V1 — the code to
   * actually pass as ExperienceSearchParams.city_iata when searching this
   * destination. Equals `iata_code` when the destination has its own;
   * otherwise the nearest real airport (e.g. "GUA" for a colonial town
   * near Guatemala City) — but the destination's OWN display_name/
   * city_name must still be sent as `city`, so the search never silently
   * becomes a search for the nearby city instead. Same iata/nearest_
   * airport_iata split lib/api.ts's own AutocompleteSuggestion already
   * uses — never a new convention. null only when the catalog has no
   * usable code at all for this destination.
   */
  nearest_airport_iata: string | null;
  match_type: DestinationMatchType;
  confidence: number;
  ambiguity_group: string | null;
  source: string;
  is_curated: boolean;
  reason_code: string;
  /**
   * Sprint: Destination Marketplace Identity vs Nearest Airport Separation
   * V1, Parte B (frontend mirror) — additive fields separating the
   * destination's OWN marketplace identity from any nearby/borrowed
   * airport code. Every field above this point is unchanged for backward
   * compatibility; these are new.
   */
  destination_id: string;
  destination_display_name: string;
  /** The name a search should actually use — the real tourist destination,
   *  never a nearby airport's own city. */
  marketplace_search_name: string;
  /** Null only when this destination has no own IATA (see has_own_iata). */
  marketplace_canonical_iata: string | null;
  /** Same value as `iata_code` — kept for naming parity with the backend
   *  contract; use whichever reads clearer at the call site. */
  own_iata_code: string | null;
  airport_codes: string[];
  has_own_iata: boolean;
  /** True when `nearest_airport_iata` is a borrowed code (this destination
   *  has no own IATA) — never true when `has_own_iata` is true. */
  is_airport_proxy: boolean;
  identity_kind: "marketplace_city" | "city_with_nearest_airport" | "airport_to_marketplace_city";
}

export interface DestinationSearchResult {
  success: boolean;
  query: string;
  candidates: DestinationCandidate[];
  error?: string;
}

/**
 * Search RHEA's own expanded destination catalog for candidates matching
 * a free-text query — never activities, never provider/booking data.
 * Calls GET /destinations/search. No auth required. May return MULTIPLE
 * candidates for a genuinely ambiguous query (e.g. a short nickname that
 * could mean either of two real destinations) — a caller must never
 * assume `candidates[0]` is automatically correct just because it sorts
 * first; check `ambiguity_group` (candidates sharing one are alternatives
 * for the SAME query, not independent unrelated matches) and `confidence`
 * before auto-selecting.
 *
 * @param q      Free-text destination query, e.g. "guate", "moscu", "ZAG".
 * @param limit  Max candidates to return (default 8, max 20).
 */
export async function searchDestinationCandidates(
  q: string,
  limit = 8
): Promise<DestinationSearchResult> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  return fetchApi(`/destinations/search?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// T1 "Coming Soon" response — returned by /itinerary/generate for resolver_only destinations
// ---------------------------------------------------------------------------

export interface DestinationCoverageCity {
  iata: string;
  name: string;
  country: string;
  country_iso2: string | null;
  continent_id: string | null;
  lat: number | null;
  lng: number | null;
  activity_count: number | null;
  provider_count: number | null;
  coverage_tier: string | null;
  observed_at: string | null;
  count_source: "latest_observed_search" | "unknown";
}

export interface DestinationCoverageCountry {
  iso2: string | null;
  name: string;
  name_en: string;
  continent_id: string | null;
  activity_count: number | null;
  city_count: number;
  observed_city_count: number;
  count_is_complete: boolean;
  cities: DestinationCoverageCity[];
}

export interface DestinationExploreResult {
  success: boolean;
  scope: {
    type: LocationResolvedPart["matched_type"];
    name: string;
    country_iso2: string | null;
    continent_id: string | null;
    continent_name: string | null;
    primary_iata: string | null;
  };
  summary: {
    country_count: number;
    city_count: number;
    observed_city_count: number;
    activity_count: number | null;
    count_is_complete: boolean;
  };
  countries: DestinationCoverageCountry[];
}

export async function exploreDestinations(
  scope: string,
): Promise<DestinationExploreResult> {
  const params = new URLSearchParams({ q: scope });
  return fetchApi(`/marketplace/destinations/explore?${params.toString()}`);
}

// ── Semantic Intent Filters + Reference Currency Display V1.1, Parte F ──
// Reference-only conversion rates, base USD. NEVER a contractual price —
// see src/lib/experiences/currencyConversion.ts for how these rates are
// applied to a card's own original_amount/original_currency (always kept
// alongside the converted display value, never overwritten).
export interface FxRatesResponse {
  base: "USD";
  rates: { USD: number; COP: number; EUR: number; BRL: number };
  as_of: string;
  source: "external_cache" | "memory_cache" | "fallback_static";
  ttl_seconds: number;
}

export async function getFxRates(): Promise<FxRatesResponse> {
  return fetchApi<FxRatesResponse>("/marketplace/fx-rates");
}

export interface T1Alternative {
  type: string;
  entity_type: EntityType;
  label: string;
  label_en: string;
  reason: string;
  destination: string;
  iata: string | null;
  country_iso2: string | null;
  continent_id: string | null;
  planning_tier: PlanningTier;
  popularity_score: number;
}

export interface T1ItineraryResponse {
  success: false;
  planning_tier: "resolver_only";
  matched_name: string;
  entity_type: EntityType;
  country_iso2: string | null;
  continent_id: string | null;
  message: string;
  message_detail: string;
  suggested_alternatives: T1Alternative[];
}

export async function notifyWaitlist(params: {
  destination: string;
  destination_en?: string;
  country_iso2?: string;
  continent_id?: string;
  email?: string;
}): Promise<{ success: boolean; message: string; destination: string }> {
  return fetchApi("/notify/waitlist", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

// ---------------------------------------------------------------------------
// Editable Timeline v1 — /itinerary/mutate
// ---------------------------------------------------------------------------

export type MutationType =
  | "add_days"
  | "remove_days"
  | "shift_leg"
  | "extend_trip"
  | "insert_stop";

export interface MutationCommand {
  type:             MutationType;
  /** Target city IATA (add_days, remove_days) */
  city_iata?:       string;
  /** Leg index (shift_leg: leg between stays[leg_index-1] and stays[leg_index]) */
  leg_index?:       number;
  /** How many days to add / remove (add_days, remove_days) */
  delta?:           number;
  /** Absolute target total days (extend_trip) */
  target_days?:     number;
  /** New city IATA to insert (insert_stop) */
  new_city_iata?:   string;
  /** Insert after this city IATA (insert_stop) */
  after_city_iata?: string;
  /** How many nights for the inserted city (insert_stop, default 2) */
  nights?:          number;
}

export interface MutateItineraryParams {
  mutation:             MutationCommand;
  /** Legacy flat arrays — kept for backward compatibility. */
  stays:                unknown[];
  legs:                 unknown[];
  days:                 unknown[];
  trip_timeline?:       unknown;
  mobility_preference?: string;
  /** Backend Authority Sprint — preferred over flat arrays when present. */
  canonical_itinerary?: unknown;
}

export interface MutateItineraryResponse {
  success:              boolean;
  error?:               string;
  /** Legacy flat arrays — still returned for backward compatibility. */
  stays?:               unknown[];
  legs?:                unknown[];
  days?:                unknown[];
  trip_timeline?:       unknown;
  mutation_summary?:    string;
  /** Backend Authority Sprint — canonical itinerary after mutation. */
  canonical_itinerary?: import("./types").CanonicalItinerary;
}

export async function mutateItinerary(
  params: MutateItineraryParams,
): Promise<MutateItineraryResponse> {
  return fetchApi<MutateItineraryResponse>("/itinerary/mutate", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

// ─── Experiences V1 ───────────────────────────────────────────────────────────

export interface ExperienceSequenceParams {
  city:            string;
  city_iata:       string;
  days:            number;
  interests:       string[];
  travelers?:      number;
  /** "solo" | "pareja" | "amigos" | "familia" — from Guided Builder */
  travelers_type?: string;
  /** "suave" | "equilibrado" | "intenso" — from Guided Builder */
  pace?:           string;
  /** "economico" | "equilibrado" | "premium" | "lujo" — from Guided Builder or profile */
  budget_style?:   string;
  budget_usd?:     number;
  /**
   * Region Discovery V1 — canonical region key (e.g. "portugal", "japon").
   * When set, the backend uses this as a direct hint to select multi-city route
   * variants instead of trying to detect the region from the city string.
   * Optional — backward compatible.
   */
  region?:         string;
  /**
   * Intent Persistence Layer V1 — snapshot of the traveler's original intent
   * captured during the fallback flow. When set, the backend uses
   * original_query for archetype matching instead of req.city, ensuring the
   * original search (e.g. "Milan vinedos romance") drives the sequence — not
   * just the selected candidate city name (e.g. "Val d'Orcia").
   * Optional — backward compatible. Null clears any pending snapshot.
   */
  intent_snapshot?: import("./types").IntentSnapshot | null;
  /**
   * Sprint 5B — Skyscanner-style date picker.
   * ISO-8601 date string (YYYY-MM-DD) for exact dates, "YYYY-MM" for flexible
   * month selection, or omitted/null for no-date mode.
   * When set, the backend activates the availability gate.
   * When null/omitted, all provider items get availability_status="skipped_no_date".
   */
  travel_date?:    string | null;
  /**
   * Sprint 8B — Traveler family profile.
   * When has_children=true, the backend applies hard-excludes on nightlife /
   * pub-tour / adult-only categories and boosts family-friendly experiences.
   * children_ages stores individual ages (IATA standard: 0-1 infante, 2-11 niño,
   * 12-17 joven). Used now for scoring; later for hotel/flight fare calculation.
   */
  has_children?:   boolean;
  children_ages?:  number[];
  /**
   * Sprint 12B — Mobility level for the traveler.
   * "normal"  → no restrictions (default)
   * "low"     → prefer low-walking options, avoid strenuous activities
   * "minimal" → full accessibility required (wheelchair-friendly)
   * When set, the backend will apply mobility-aware scoring in Sprint 12D.
   */
  mobility_level?: "normal" | "low" | "minimal" | null;
  /**
   * Supplier Intelligence QA V1 — when true, attaches supplier_intelligence_debug
   * to the response (top SI score, top title, risk flags).
   * Never included in production calls — set automatically in DEV mode only.
   * Default: false. Never exposes raw_payload or secrets.
   */
  debug?: boolean;
}

export async function generateExperienceSequence(
  params: ExperienceSequenceParams,
): Promise<import("./types").ExperienceSequence> {
  return fetchApi<import("./types").ExperienceSequence>("/experiences/sequence", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

// ─── Marketplace Inventory Scale & Discovery V1 ─────────────────────────────
// POST /experiences/search — dedicated, paginated marketplace-search
// contract. experiences.tsx calls this for the marketplace list; falls back
// to /experiences/sequence's marketplace_inventory when this fails or the
// backend hasn't deployed it yet (404/network error) — see
// src/routes/experiences.tsx's runMarketplaceSearch().
export async function searchExperiences(
  params: import("./types").ExperienceSearchParams,
): Promise<import("./types").ExperienceSearchResponse> {
  return fetchApi<import("./types").ExperienceSearchResponse>("/experiences/search", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

// ─── Viator Product Detail V1 ─────────────────────────────────────────────────

/**
 * Fetch rich product detail for a single Viator product via the RHEA backend proxy.
 *
 * Calls GET /providers/viator/products/{productCode}.
 * Authorization header is included when a Supabase session exists.
 * Never logs the token. Never calls Viator directly.
 *
 * @param productCode   Viator product identifier (e.g. "6476P16"). Must be non-empty.
 * @param marketLocale  Hotfix: Provider Detail Locale Wiring V1 — "es-CO" |
 *                      "en-US" | "pt-BR" (see usePreferences.ts's own
 *                      languageToMarketLocale()). Forwarded as-is via the
 *                      market_locale query param; the backend normalizes it
 *                      and uses it for Viator's Accept-Language header, so
 *                      title/description/booking_url all come back in that
 *                      language. Omitted defaults to "es-CO" server-side.
 * @returns ViatorProductDetail — safe mapped fields only, no raw payload.
 * @throws Error when productCode is blank or the backend returns an error.
 */
export async function getViatorProductDetail(
  productCode: string,
  marketLocale?: string,
): Promise<import("./types").ViatorProductDetail> {
  const pc = productCode.trim();
  if (!pc) throw new Error("getViatorProductDetail: productCode must not be blank");
  const query = marketLocale
    ? `?market_locale=${encodeURIComponent(marketLocale)}`
    : "";
  return fetchApi<import("./types").ViatorProductDetail>(
    `/providers/viator/products/${encodeURIComponent(pc)}${query}`,
  );
}

// ─── Fallback Intelligence V1 ─────────────────────────────────────────────────

/**
 * Called when the user queries a city not in RHEA's catalog.
 * Returns ranked fallback destination candidates matching the inferred intent.
 */
export async function fetchFallbackCandidates(
  q: string,
): Promise<import("./types").FallbackResult> {
  return fetchApi<import("./types").FallbackResult>(
    `/experiences/fallback?q=${encodeURIComponent(q)}`,
  );
}

// ─── Provider Destination Discovery V2 ───────────────────────────────────────

/**
 * Discovers destination candidates backed by real provider product signals.
 * Called when exact city parsing fails and fallback intelligence finds nothing.
 *
 * Calls GET /discovery/destinations — never calls Viator directly.
 * Never returns booking_url or product_url.
 * Authorization header is included when a Supabase session exists.
 *
 * @param query  Free-text travel intent, e.g. "milan viñedos romance"
 * @param limit  Max candidates to return (default 8, max 20)
 */
export async function getProviderDestinations(
  query: string,
  limit = 8,
): Promise<import("./types").ProviderDestinationDiscoveryResult> {
  const params = new URLSearchParams({
    q:     query,
    limit: String(limit),
    score: "true",
  });
  return fetchApi<import("./types").ProviderDestinationDiscoveryResult>(
    `/discovery/destinations?${params.toString()}`,
  );
}

// ── Sprint 13B — LLM intent parser ────────────────────────────────────────────

/** Response from POST /parse/intent — superset of ParsedQuery. */
export interface LLMParsedIntent {
  city:            string;
  iata:            string;
  days:            number;
  hasDays:         boolean;
  interests:       string[];
  pace:            "suave" | "equilibrado" | "intenso" | null;
  budget:          "economico" | "equilibrado" | "premium" | "lujo" | null;
  mobilityLevel:   "normal" | "low" | "minimal";
  travelersType:   "solo" | "pareja" | "familia" | "amigos" | "grupo" | null;
  travel_date:     string | null;
  region:          string | null;
  regionLabel:     string | null;
  interpretations: Array<{ raw: string; as: string; label: string }>;
  ambiguities:     string[];
  follow_up:       string | null;
  parsed_by:       "llm";
}

/**
 * Call the backend LLM intent parser (Claude Haiku).
 * Resolves to null on network/timeout/parse errors — caller falls back to regex.
 * Times out after 5 s to avoid blocking the search flow.
 */
export async function parseIntent(query: string): Promise<LLMParsedIntent | null> {
  const ctrl = new AbortController();
  const timeout = setTimeout(() => ctrl.abort(), 5_000);
  try {
    const result = await fetchApi<LLMParsedIntent>("/parse/intent", {
      method:  "POST",
      body:    JSON.stringify({
        query,
        current_date: new Date().toISOString().split("T")[0],
      }),
      signal: ctrl.signal,
    });
    return result;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

